import React, { Suspense, lazy, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { AuthProvider } from './contexts/AuthContext'
import { BrandingProvider } from './contexts/BrandingContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { ToastProvider } from './components/Toast'
import ProtectedRoute from './components/ProtectedRoute'
import Sidebar from './components/Sidebar'
import Navbar from './components/Navbar'
import Footer from './components/Footer'
import ErrorFallback from './components/ErrorFallback'
import StaffAlertsProvider from './components/notifications/StaffAlertsProvider'
import {
  LEGACY_ROLES,
  MANAGEMENT_ROLES,
  OPERATIONAL_ROLES,
  ADMIN_ONLY_ROLES,
  DASHBOARD_ROLES,
  LEGACY_MANAGER_DASHBOARD_ROLES,
} from './constants/roles'

// Small wrapper so we can drop a per-route boundary around the high-traffic
// pages without having to repeat the props. The `key` resets the boundary
// when the route changes — without it, the user gets stuck on the error UI
// even after navigating away.
const RouteErrorBoundary = ({ children, routeKey }) => (
  <ErrorBoundary
    FallbackComponent={ErrorFallback}
    onError={(err) => console.error('[route-boundary] uncaught', err)}
    resetKeys={[routeKey]}
  >
    {children}
  </ErrorBoundary>
)

// Lazy load pages for code splitting
const Login = lazy(() => import('./pages/Login'))
const NotAuthorized = lazy(() => import('./pages/NotAuthorized'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const ManagerDashboard = lazy(() => import('./pages/manager/ManagerDashboard'))
const EmployeeDashboard = lazy(() => import('./pages/employee/EmployeeDashboard'))
const Villas = lazy(() => import('./pages/Villas'))
const VillaDetail = lazy(() => import('./pages/VillaDetail'))
const VillaDashboard = lazy(() => import('./pages/VillaDashboard'))
const Agents = lazy(() => import('./pages/Agents'))
const AgentProfile = lazy(() => import('./pages/AgentProfile'))
const Owners = lazy(() => import('./pages/Owners'))
const OwnerProfile = lazy(() => import('./pages/OwnerProfile'))
const Vendors = lazy(() => import('./pages/Vendors'))
const VendorProfile = lazy(() => import('./pages/VendorProfile'))
const Agencies = lazy(() => import('./pages/Agencies'))
const AgencyProfile = lazy(() => import('./pages/AgencyProfile'))
const Duplicates = lazy(() => import('./pages/Duplicates'))
const DataQuality = lazy(() => import('./pages/DataQuality'))
const Favorites = lazy(() => import('./pages/Favorites'))
const VillaResearch = lazy(() => import('./pages/VillaResearch'))
import CommandPalette from './components/CommandPalette'
import LilliButton from './components/LilliButton'
const DataImport = lazy(() => import('./pages/DataImport'))
const SeasonsEditor = lazy(() => import('./pages/SeasonsEditor'))
const AdminTemplateDetail = lazy(() => import('./pages/AdminTemplateDetail'))
const ContractClausesAdmin = lazy(() => import('./pages/admin/ContractClausesAdmin'))
const ContractTemplatesAdmin = lazy(() => import('./pages/admin/ContractTemplatesAdmin'))
const SettingsHome = lazy(() => import('./pages/SettingsHome'))
const SettingsTemplatesList = lazy(() => import('./pages/SettingsTemplatesList'))
const SettingsTemplateDetail = lazy(() => import('./pages/SettingsTemplateDetail'))
const SettingsBranding = lazy(() => import('./pages/SettingsBranding'))
const SignatureEditor = lazy(() => import('./components/settings/SignatureEditor'))
const SystemSettings = lazy(() => import('./pages/SystemSettings'))
const WpSyncSettings = lazy(() => import('./pages/WpSyncSettings'))
const ConnectedMailboxes = lazy(() => import('./pages/ConnectedMailboxes'))
const Inbox = lazy(() => import('./pages/Inbox'))
const AvailabilityManagement = lazy(() => import('./pages/AvailabilityManagement'))
const ExclusiveDashboard = lazy(() => import('./pages/ExclusiveDashboard'))
const ExclusiveReports = lazy(() => import('./pages/ExclusiveReports'))
const OwnerPayoutDashboard = lazy(() => import('./pages/OwnerPayoutDashboard'))
const OwnerFinancialReport = lazy(() => import('./pages/OwnerFinancialReport'))
const NonExclusiveBookingsDashboard = lazy(() => import('./pages/NonExclusiveBookingsDashboard'))
const NonExclusivePayouts = lazy(() => import('./pages/NonExclusivePayouts'))
const NonExclusiveReports = lazy(() => import('./pages/NonExclusiveReports'))
const AllTransactions = lazy(() => import('./pages/AllTransactions'))
const GlobalOwnerPayouts = lazy(() => import('./pages/GlobalOwnerPayouts'))
const AgentCommissions = lazy(() => import('./pages/AgentCommissions'))
const LiveChats = lazy(() => import('./pages/LiveChats'))
const ReportsExport = lazy(() => import('./pages/ReportsExport'))
const ClientsDashboard = lazy(() => import('./pages/ClientsDashboard'))
const ClientDetail = lazy(() => import('./pages/ClientDetail'))
const LeadsDashboard = lazy(() => import('./pages/LeadsDashboard'))
const LeadDetail = lazy(() => import('./pages/LeadDetail'))
const GuestPreferences = lazy(() => import('./pages/GuestPreferences'))
const UserManagement = lazy(() => import('./pages/UserManagement'))
const AuditLog = lazy(() => import('./pages/admin/AuditLog'))
const UserProfile = lazy(() => import('./pages/UserProfile'))
const Approvals = lazy(() => import('./pages/Approvals'))
const ApprovalDetail = lazy(() => import('./pages/ApprovalDetail'))
const ContactApprovalDetail = lazy(() => import('./pages/ContactApprovalDetail'))
const ExclusiveCalendars = lazy(() => import('./pages/ExclusiveCalendars'))
const Calendar = lazy(() => import('./pages/Calendar'))

// Loading spinner component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-4 border-[#cfa190] border-t-transparent rounded-full animate-spin" />
      <span className="text-sm text-slate-500 dark:text-slate-400">Loading...</span>
    </div>
  </div>
)

// Shared layout wrapper for protected routes
const AppLayout = ({ children }) => (
  <StaffAlertsProvider>
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-200 overflow-x-hidden">
      <Sidebar />
      <div className="flex-1 min-w-0 ml-0 lg:ml-64 flex flex-col overflow-x-hidden">
        <Navbar />
        <main className="flex-1 min-w-0 dark:bg-slate-900 overflow-x-hidden">
          <Suspense fallback={<PageLoader />}>
            {children}
          </Suspense>
        </main>
        <Footer />
      </div>
    </div>
  </StaffAlertsProvider>
)

// Protected route with layout
const ProtectedPage = ({ children, allowedRoles }) => (
  <ProtectedRoute allowedRoles={allowedRoles}>
    <AppLayout>{children}</AppLayout>
  </ProtectedRoute>
)

// Component to redirect based on user role
function RoleBasedRedirect() {
  const userStr = localStorage.getItem("user")
  const token = localStorage.getItem("token")

  if (!token || !userStr) {
    return <Navigate to="/login" replace />
  }

  try {
    const user = JSON.parse(userStr)
    // All roles go to main dashboard
    if (user.role === LEGACY_ROLES.MANAGER) {
      return <Navigate to="/manager/dashboard" replace />
    } else {
      return <Navigate to="/dashboard" replace />
    }
  } catch (e) {
    console.error("Error parsing user:", e)
    return <Navigate to="/login" replace />
  }
}

function App() {
  return (
    // Top-level boundary: catches anything that escapes per-route boundaries,
    // including bugs in the providers below us. Without this, a render-time
    // crash in (say) AuthProvider would blank the whole page.
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(err) => console.error('[app-boundary] uncaught', err)}
    >
    <ThemeProvider>
      <AuthProvider>
        <BrandingProvider>
          <ToastProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <CommandPalette />
            {/* Internal Lilli AI assistant — floating button + chat panel.
                Only renders for allow-listed emails (handled inside the
                component); safe to mount globally. */}
            <LilliButton />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/not-authorized" element={<NotAuthorized />} />

                {/* Role-based dashboard routes */}
                {/* Admin dashboard redirects to main dashboard */}
                <Route path="/admin/dashboard" element={<Navigate to="/dashboard" replace />} />
                <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
                <Route path="/manager/dashboard" element={
                  <ProtectedPage allowedRoles={LEGACY_MANAGER_DASHBOARD_ROLES}><ManagerDashboard /></ProtectedPage>
                } />
                <Route path="/manager" element={
                  <ProtectedPage allowedRoles={LEGACY_MANAGER_DASHBOARD_ROLES}><ManagerDashboard /></ProtectedPage>
                } />
                <Route path="/dashboard" element={
                  <ProtectedPage allowedRoles={DASHBOARD_ROLES}><RouteErrorBoundary routeKey="dashboard"><Dashboard /></RouteErrorBoundary></ProtectedPage>
                } />
                <Route path="/employee-dashboard" element={
                  <ProtectedPage allowedRoles={DASHBOARD_ROLES}><EmployeeDashboard /></ProtectedPage>
                } />

                {/* Root redirect */}
                <Route path="/" element={<RoleBasedRedirect />} />

                {/* Villa routes */}
                <Route path="/villas" element={<ProtectedPage><Villas /></ProtectedPage>} />
                <Route path="/add-villa" element={<ProtectedPage><Villas /></ProtectedPage>} />
                <Route path="/villas/:id" element={<ProtectedPage><RouteErrorBoundary routeKey="villa-detail"><VillaDetail /></RouteErrorBoundary></ProtectedPage>} />
                <Route path="/villas/:id/dashboard" element={<ProtectedPage><VillaDashboard /></ProtectedPage>} />
                <Route path="/exclusive-villas" element={<ProtectedPage><Villas /></ProtectedPage>} />
                <Route path="/non-exclusive-villas" element={<ProtectedPage><Villas /></ProtectedPage>} />

                {/* Agent routes */}
                <Route path="/agents" element={<ProtectedPage><Agents /></ProtectedPage>} />
                <Route path="/agents/:id" element={<ProtectedPage><AgentProfile /></ProtectedPage>} />

                {/* Owner routes */}
                <Route path="/owners" element={<ProtectedPage><Owners /></ProtectedPage>} />
                <Route path="/owners/:id" element={<ProtectedPage><OwnerProfile /></ProtectedPage>} />

                {/* Vendor routes */}
                <Route path="/vendors" element={<ProtectedPage><Vendors /></ProtectedPage>} />
                <Route path="/vendors/:id" element={<ProtectedPage><VendorProfile /></ProtectedPage>} />

                {/* Agency routes */}
                <Route path="/agencies" element={<ProtectedPage><Agencies /></ProtectedPage>} />
                <Route path="/agencies/:id" element={<ProtectedPage><AgencyProfile /></ProtectedPage>} />

                {/* Live Chat console — M2 of 002-live-chat */}
                {/* C-6: gate console to chat-handling roles. Without this any
                    authenticated user (including future limited roles) gets the
                    full operator UI with bulk-delete / assignment / notes. */}
                <Route path="/live-chats" element={<ProtectedPage allowedRoles={OPERATIONAL_ROLES}><LiveChats /></ProtectedPage>} />

                <Route path="/duplicates" element={<ProtectedPage><Duplicates /></ProtectedPage>} />
                <Route path="/data-quality" element={<ProtectedPage><DataQuality /></ProtectedPage>} />
                <Route path="/favorites" element={<ProtectedPage><Favorites /></ProtectedPage>} />
                <Route path="/villa-research" element={<ProtectedPage><VillaResearch /></ProtectedPage>} />

                {/* Admin/Settings routes */}
                <Route path="/import" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><DataImport /></ProtectedPage>} />
                <Route path="/tiers/:tierId/seasons" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><SeasonsEditor /></ProtectedPage>} />
                <Route path="/admin/templates" element={<Navigate to="/settings/templates" replace />} />
                <Route path="/admin/templates/:templateId" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><AdminTemplateDetail /></ProtectedPage>} />
                <Route path="/admin/contract-clauses" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><ContractClausesAdmin /></ProtectedPage>} />
                <Route path="/admin/contract-templates" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><ContractTemplatesAdmin /></ProtectedPage>} />
                <Route path="/settings" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><SettingsHome /></ProtectedPage>} />
                <Route path="/settings/templates" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><SettingsTemplatesList /></ProtectedPage>} />
                <Route path="/settings/templates/:templateId" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><SettingsTemplateDetail /></ProtectedPage>} />
                <Route path="/settings/branding" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><SettingsBranding /></ProtectedPage>} />
                <Route path="/settings/email-signature" element={<ProtectedPage allowedRoles={OPERATIONAL_ROLES}><SignatureEditor /></ProtectedPage>} />
                <Route path="/settings/users" element={<ProtectedPage allowedRoles={ADMIN_ONLY_ROLES}><UserManagement /></ProtectedPage>} />
                <Route path="/admin/audit-log" element={<ProtectedPage allowedRoles={ADMIN_ONLY_ROLES}><AuditLog /></ProtectedPage>} />
                <Route path="/settings/system" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><SystemSettings /></ProtectedPage>} />
                <Route path="/settings/wp-sync" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><WpSyncSettings /></ProtectedPage>} />
                <Route path="/settings/connected-mailboxes" element={<ProtectedPage allowedRoles={OPERATIONAL_ROLES}><ConnectedMailboxes /></ProtectedPage>} />
                <Route path="/inbox" element={<ProtectedPage allowedRoles={OPERATIONAL_ROLES}><Inbox /></ProtectedPage>} />

                {/* Rate change approvals */}
                <Route path="/approvals" element={<ProtectedPage><Approvals /></ProtectedPage>} />
                <Route path="/approvals/contact/:id" element={<ProtectedPage><ContactApprovalDetail /></ProtectedPage>} />
                <Route path="/approvals/:id" element={<ProtectedPage><ApprovalDetail /></ProtectedPage>} />

                {/* Exclusive villas calendar overview */}
                <Route path="/exclusive-calendars" element={<ProtectedPage><ExclusiveCalendars /></ProtectedPage>} />

                {/* Reservation Operations Calendar — agenda + month, backed by /api/agenda */}
                <Route path="/calendar" element={<ProtectedPage allowedRoles={OPERATIONAL_ROLES}><RouteErrorBoundary routeKey="calendar"><Calendar /></RouteErrorBoundary></ProtectedPage>} />

                {/* User Profile */}
                <Route path="/profile" element={<ProtectedPage><UserProfile /></ProtectedPage>} />

                {/* Availability */}
                <Route path="/availability" element={<ProtectedPage><AvailabilityManagement /></ProtectedPage>} />

                {/* Exclusive routes */}
                <Route path="/exclusive-dashboard" element={<ProtectedPage><RouteErrorBoundary routeKey="exclusive-dashboard"><ExclusiveDashboard /></RouteErrorBoundary></ProtectedPage>} />
                <Route path="/exclusive-reports" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><ExclusiveReports /></ProtectedPage>} />
                <Route path="/owner-payouts" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><OwnerPayoutDashboard /></ProtectedPage>} />

                {/* Non-Exclusive routes */}
                <Route path="/non-exclusive-bookings-dashboard" element={<ProtectedPage><RouteErrorBoundary routeKey="non-exclusive-dashboard"><NonExclusiveBookingsDashboard /></RouteErrorBoundary></ProtectedPage>} />
                <Route path="/non-exclusive-payouts" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><NonExclusivePayouts /></ProtectedPage>} />
                <Route path="/non-exclusive-reports" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><NonExclusiveReports /></ProtectedPage>} />

                {/* CRM routes */}
                <Route path="/clients" element={<ProtectedPage><ClientsDashboard /></ProtectedPage>} />
                <Route path="/clients/new" element={<ProtectedPage><ClientDetail /></ProtectedPage>} />
                <Route path="/clients/:id" element={<ProtectedPage><ClientDetail /></ProtectedPage>} />
                <Route path="/leads" element={<ProtectedPage><RouteErrorBoundary routeKey="leads"><LeadsDashboard /></RouteErrorBoundary></ProtectedPage>} />
                <Route path="/leads/:id" element={<ProtectedPage><LeadDetail /></ProtectedPage>} />
                <Route path="/guest-preferences" element={<ProtectedPage allowedRoles={OPERATIONAL_ROLES}><RouteErrorBoundary routeKey="guest-preferences"><GuestPreferences /></RouteErrorBoundary></ProtectedPage>} />
                <Route path="/guest-preferences/:id" element={<ProtectedPage allowedRoles={OPERATIONAL_ROLES}><RouteErrorBoundary routeKey="guest-preference-detail"><GuestPreferences /></RouteErrorBoundary></ProtectedPage>} />

                {/* Financial routes */}
                <Route path="/transactions" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><AllTransactions /></ProtectedPage>} />
                <Route path="/global-owner-payouts" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><GlobalOwnerPayouts /></ProtectedPage>} />
                <Route path="/agent-commissions" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><AgentCommissions /></ProtectedPage>} />
                <Route path="/reports-export" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><ReportsExport /></ProtectedPage>} />

                {/* Legacy routes */}
                <Route path="/owner-report" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><OwnerFinancialReport /></ProtectedPage>} />
                <Route path="/owner-report/:reportId" element={<ProtectedPage allowedRoles={MANAGEMENT_ROLES}><OwnerFinancialReport /></ProtectedPage>} />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>
            </Router>
          </ToastProvider>
        </BrandingProvider>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  )
}

export default App
